import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Editor,
    EditorState,
    RichUtils,
    convertToRaw,
    convertFromHTML,
    ContentState,
    Modifier,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import {
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
} from "lucide-react";

const styleMap = {
    BOLD: { fontWeight: "bold" },
    ITALIC: { fontStyle: "italic" },
    UNDERLINE: { textDecoration: "underline" },
};

function htmlToEditorState(html) {
    if (!html) return EditorState.createEmpty();
    const blocksFromHTML = convertFromHTML(html.replace(/\u200F/g, ""));
    const content = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
    );
    return EditorState.createWithContent(content);
}

const RichTextEditor = ({ value, onChange, label, labelClassName }) => {
    const [editorState, setEditorState] = useState(() =>
        htmlToEditorState(value)
    );
    const lastValueRef = useRef(value);
    const isInternalChange = useRef(false);

    const handleChangeCallback = useCallback(
        (html) => {
            isInternalChange.current = true;
            onChange(html);
            setTimeout(() => {
                isInternalChange.current = false;
            }, 0);
        },
        [onChange]
    );

    useEffect(() => {
        if (value !== lastValueRef.current && !isInternalChange.current) {
            setEditorState(htmlToEditorState(value));
            lastValueRef.current = value;
        }
    }, [value]);

    const handleChange = (state) => {
        setEditorState(state);
        const raw = convertToRaw(state.getCurrentContent());
        const html = draftToHtml(raw).replace(/\u200F/g, "");
        handleChangeCallback(html);
    };

    const handleKeyCommand = (command) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            handleChange(newState);
            return "handled";
        }
        return "not-handled";
    };

    // Inline styles (bold, italic, underline)
    const toggleInlineStyle = (style) => {
        handleChange(RichUtils.toggleInlineStyle(editorState, style));
    };

    // Block styles (header, list)
    const toggleBlockType = (blockType) => {
        handleChange(RichUtils.toggleBlockType(editorState, blockType));
    };

    // Alignment
    const applyAlignment = (alignment) => {
        const selection = editorState.getSelection();
        const content = editorState.getCurrentContent();
        const blockKey = selection.getStartKey();
        const block = content.getBlockForKey(blockKey);
        const blockData = block.getData();

        const newData = blockData.set("text-align", alignment);
        const newContent = Modifier.setBlockData(content, selection, newData);
        const newState = EditorState.push(editorState, newContent, "change-block-data");
        handleChange(EditorState.forceSelection(newState, selection));
    };

    const currentStyle = editorState.getCurrentInlineStyle();
    const currentBlockType = editorState
        .getCurrentContent()
        .getBlockForKey(editorState.getSelection().getStartKey())
        .getType();

    const currentAlignment =
        editorState
            .getCurrentContent()
            .getBlockForKey(editorState.getSelection().getStartKey())
            .getData()
            .get("text-align") || "left";

    // --- Styles ---
    const editorBoxStyle = {
        minHeight: "300px",
        maxHeight: "400px",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        padding: "8px 10px",
        fontSize: "14px",
        color: "#5d6c7d",
        fontWeight: 400,
        backgroundColor: "#fff",
        overflowY: "auto",
    };

    const toolbarStyle = {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        marginBottom: "10px",
        alignItems: "center",
    };

    const buttonStyle = {
        background: "none",
        border: "none",
        color: "#5d6c7d",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        borderRadius: "6px",
        padding: "4px 6px",
        height: "26px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    const activeButtonStyle = {
        backgroundColor: "rgb(210 232 255)",
    };

    const blockStyleFn = (contentBlock) => {
        const alignment = contentBlock.getData().get("text-align");
        switch (alignment) {
            case "center":
                return "align-center";
            case "right":
                return "align-right";
            case "justify":
                return "align-justify";
            default:
                return "align-left";
        }
    };

    return (
        <div>
            {/* {label && <label className={labelClassName}>{label}</label>} */}

            <div style={toolbarStyle}>
                {/* Inline Styles */}
                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleInlineStyle("BOLD");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentStyle.has("BOLD") ? activeButtonStyle : {}),
                    }}
                >
                    <b>B</b>
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleInlineStyle("ITALIC");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentStyle.has("ITALIC") ? activeButtonStyle : {}),
                    }}
                >
                    <i>I</i>
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleInlineStyle("UNDERLINE");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentStyle.has("UNDERLINE") ? activeButtonStyle : {}),
                    }}
                >
                    <u>U</u>
                </button>

                {/* Headings */}
                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleBlockType("header-one");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentBlockType === "header-one" ? activeButtonStyle : {}),
                    }}
                >
                    H1
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleBlockType("header-two");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentBlockType === "header-two" ? activeButtonStyle : {}),
                    }}
                >
                    H2
                </button>

                {/* Lists */}
                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleBlockType("unordered-list-item");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentBlockType === "unordered-list-item"
                            ? activeButtonStyle
                            : {}),
                    }}
                >
                    <List size={16} />
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleBlockType("ordered-list-item");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentBlockType === "ordered-list-item"
                            ? activeButtonStyle
                            : {}),
                    }}
                >
                    <ListOrdered size={16} />
                </button>

                {/* Alignment */}
                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyAlignment("left");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentAlignment === "left" ? activeButtonStyle : {}),
                    }}
                >
                    <AlignLeft size={16} />
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyAlignment("center");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentAlignment === "center" ? activeButtonStyle : {}),
                    }}
                >
                    <AlignCenter size={16} />
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyAlignment("right");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentAlignment === "right" ? activeButtonStyle : {}),
                    }}
                >
                    <AlignRight size={16} />
                </button>

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyAlignment("justify");
                    }}
                    style={{
                        ...buttonStyle,
                        ...(currentAlignment === "justify" ? activeButtonStyle : {}),
                    }}
                >
                    <AlignJustify size={16} />
                </button>
            </div>

            <div style={editorBoxStyle}>
                <Editor
                    editorState={editorState}
                    onChange={handleChange}
                    handleKeyCommand={handleKeyCommand}
                    customStyleMap={styleMap}
                    blockStyleFn={blockStyleFn}
                    spellCheck={true}
                />
            </div>

            {/* CSS for alignment */}
            <style>
                {`
          .align-left { text-align: left; }
          .align-center { text-align: center; }
          .align-right { text-align: right; }
          .align-justify { text-align: justify; }
        `}
            </style>
        </div>
    );
};

export default RichTextEditor;
